// Auth0 v4: Error classes removed - use error.code property instead

import { Box } from '@mui/material';
import { AxiosError } from 'axios';
import type { ReactNode } from 'react';

import { ProjectSettingsError } from '@campaign-manager/core/lib/projectSettings';
import {
  getApiErrorMessage,
  Unauthenticated,
  Unauthorized,
} from '@campaign-manager/feature/lib/error';
import { Button, ErrorDetails, Prose } from '@campaign-manager/react/components';
import Text from '@campaign-manager/react/components/Text';
import { campaignManagerTheme } from '@campaign-manager/theme';

import ErrorBlock from '../ErrorBlock';

interface Props {
  error?: Error | AxiosError | Unauthenticated | Unauthorized | any;
  showPrimaryButton?: boolean;
  showReloadButton?: boolean;
}

export default function ErrorPane({ error, showPrimaryButton, showReloadButton }: Props) {
  let title = 'Error';
  let body: ReactNode | string | undefined;

  let showSecondaryCta = true;

  const details =
    error instanceof Object || error instanceof Error
      ? {
          name: error?.name,
          code: error?.code,
          message: error?.message,
          cause: JSON.stringify(error?.cause),
        }
      : undefined;

  const detailsComp = details
    ? details && (
        <ErrorDetails
          sx={{ mt: '16px!important' }}
          color={campaignManagerTheme.color.darkGrey}
          detailsColor={campaignManagerTheme.color.grey}
          details={
            <>
              Name: {details.name || 'technical interruption'}
              {Boolean(details.code) && (
                <>
                  <br />
                  Code: {details.code}
                </>
              )}
              {Boolean(details.message) && (
                <>
                  <br />
                  Message: {details.message}
                </>
              )}
              {Boolean(details.cause) && (
                <>
                  <br />
                  Cause: {details.cause}
                </>
              )}
            </>
          }
          detailsClipboardText={JSON.stringify(details)}
        />
      )
    : undefined;

  // Auth0 v4: Check error codes instead of instanceof checks
  const isAuthError =
    error instanceof Object &&
    (error.code === 'ERR_HANDLER' ||
      error.code === 'ERR_ACCESS_TOKEN' ||
      error.code === 'ERR_CALLBACK');

  if (
    isAuthError ||
    (error instanceof Object &&
      error.code === 'AUTH_INTERNAL_ERROR' &&
      error?.cause &&
      error?.cause.errorDescription === 'invitation not found or already used')
  ) {
    title = 'The invitation expired or used';
    body = (
      <Prose sx={{ textAlign: 'left' }}>
        <p>The invitation link you opened is expired or used already.</p>
        <p>Please ask the person who invited you to send you a new invitation link.</p>
        <ul>
          <li>
            If the issue persists, <a href="mailto:support@retailmediatools.com">contact support</a>{' '}
            for further assistance
          </li>
        </ul>
        <p>We apologize for the inconvenience and appreciate your understanding</p>
        {detailsComp}
      </Prose>
    );
  } else if (
    (error instanceof Object && error.code === 'ERR_CALLBACK') ||
    (error instanceof Object &&
      error.code === 'AUTH_INTERNAL_ERROR' &&
      error?.cause &&
      error?.cause.errorDescription?.startsWith('Your password has expired.'))
  ) {
    title = 'Your password has expired';
    body = (
      <Prose sx={{ textAlign: 'left' }}>
        <p>
          Your password has expired and needs to be updated. Click "Log in again" to go to the login
          page where you can reset your password.
        </p>
        <Box sx={{ my: 2, display: 'flex', gap: 2 }}>
          <Button
            href="/auth/login?force_login=true"
            variant="contained"
          >
            Log in again
          </Button>
        </Box>
      </Prose>
    );
    showPrimaryButton = false;
    showReloadButton = false;
    showSecondaryCta = false;
  } else if (
    (error instanceof Object && error.code === 'ERR_CALLBACK') ||
    (error instanceof Object &&
      error.code === 'AUTH_INTERNAL_ERROR' &&
      error?.cause &&
      new RegExp(/user .+ is not part of the .+ organization/).exec(
        error?.cause.errorDescription,
      ) !== null)
  ) {
    title = `You don't have access to the project`;
    body = (
      <Prose sx={{ textAlign: 'left' }}>
        <p>Please ask the person who manages the project to invite you.</p>
        <ul>
          <li>
            If the issue persists, <a href="mailto:support@retailmediatools.com">contact support</a>{' '}
            for further assistance
          </li>
        </ul>
        <p>We apologize for the inconvenience and appreciate your understanding</p>
        {detailsComp}
      </Prose>
    );
  } else if (isAuthError || (error instanceof Object && error.code === 'AUTH_INTERNAL_ERROR')) {
    title = 'Authentication error';
    body = (
      <Prose sx={{ textAlign: 'left' }}>
        <p>An error occurred while trying to authenticate you. Please try again</p>
        <p>
          It looks like something went wrong, and we couldn't process your request. This may be due
          to incorrect data or a technical issue on our end
        </p>
        <ul>
          <li>Please try again</li>
          <li>
            If the issue persists, <a href="mailto:support@retailmediatools.com">contact support</a>{' '}
            for further assistance
          </li>
        </ul>
        <p>We apologize for the inconvenience and appreciate your understanding</p>
        {detailsComp}
      </Prose>
    );
  } else if (error instanceof Unauthenticated) {
    title = 'You are not logged in';
    body = (
      <Prose>
        <p>There was an error authenticating the user</p>
        {detailsComp}
      </Prose>
    );
  } else if (error instanceof Unauthorized) {
    title = 'Access denied';
    body = <Prose>You don&apos;t have access to this page</Prose>;
  } else if (error instanceof ProjectSettingsError) {
    title = `Project couldn't be loaded`;
    body = (
      <Prose>
        <p>An technical error prevented this project from loading</p>
        <ul>
          <li>Please try again</li>
          <li>
            If the issue persists, <a href="mailto:support@retailmediatools.com">contact support</a>{' '}
            for further assistance
          </li>
        </ul>
        <p>We apologize for the inconvenience and appreciate your understanding</p>
        {detailsComp}
      </Prose>
    );
  } else if (error instanceof AxiosError) {
    if (error.status === 401) {
      title = 'Access denied';
      body = (
        <Prose>
          You don&apos;t have access to this resource. If your session has expired, reloading the
          page will sign you in again.
        </Prose>
      );
    } else {
      title = 'Error';
      body = (
        <Text>
          {
            getApiErrorMessage(
              error,
              'There has been a technical error. We apologize for the inconvenience',
            ).message
          }
        </Text>
      );
    }
  } else {
    title = 'Something went wrong';

    body = (
      <Prose>
        <p>There has been a technical error. We apologize for the inconvenience</p>
        {detailsComp}
      </Prose>
    );
  }

  return (
    <ErrorBlock
      title={title}
      body={body}
      {...(showPrimaryButton && {
        primaryCta: {
          label: 'Refresh the page',
          href: window.location.href,
        },
      })}
      {...(showReloadButton && {
        primaryCta: {
          label: 'Reload',
          href: window.location.href,
        },
      })}
      {...(showSecondaryCta && {
        secondaryCta: {
          label: 'Back to Home',
          href: '/',
        },
      })}
    />
  );
}
